import '../../../i18n';
import { useTranslation } from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { FaDumbbell, FaGraduationCap, FaLocationDot, FaUtensils } from 'react-icons/fa6';
import { FiBookOpen, FiCalendar, FiCheckSquare, FiChevronRight, FiFilm, FiShoppingBag } from 'react-icons/fi';

import { ENTITY_LABELS, ENTITY_PLURAL_LABELS } from '../EntityBadges';
import ArtifactDetailModal from '../../Artifacts/ArtifactDetailModal';
import { getArtifactColors } from '../../Artifacts/artifactPresentation';

const getType = artifact => {
    const type = String(artifact?.type || '').toLowerCase();
    return type === 'repair' ? 'tutorial' : type === 'movie' ? 'film' : type;
};

const iconFor = (artifact, size = 18) => ({
    event: <FiCalendar size={size} />,
    recipe: <FaUtensils size={size} />,
    workout: <FaDumbbell size={size} />,
    tutorial: <FaGraduationCap size={size} />,
    film: <FiFilm size={size} />,
    book: <FiBookOpen size={size} />,
    product: <FiShoppingBag size={size} />,
    place: <FaLocationDot size={size} />
}[getType(artifact)] || <FiCheckSquare size={size} />);

const themeFor = artifact => {
    const { text, bg, border } = getArtifactColors(artifact);
    return { color: text, background: bg, border };
};

const parseEventDate = dateStr => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const trimmed = dateStr.trim();

    // 1. YYYY-MM-DD or YYYY/MM/DD
    const isoMatch = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (isoMatch) {
        const [, y, m, d] = isoMatch;
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        if (!isNaN(date.getTime())) return { date, year: parseInt(y, 10), month: parseInt(m, 10) - 1, day: parseInt(d, 10) };
    }

    // 2. DD. MM. YYYY. (European dot notation with optional spaces and optional trailing dot, e.g. "14. 9. 2026.")
    const dotMatch = trimmed.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})\.?/);
    if (dotMatch) {
        const [, d, m, y] = dotMatch;
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        if (!isNaN(date.getTime())) return { date, year: parseInt(y, 10), month: parseInt(m, 10) - 1, day: parseInt(d, 10) };
    }

    // 3. DD/MM/YYYY or DD-MM-YYYY
    const slashMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (slashMatch) {
        const [, d, m, y] = slashMatch;
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        if (!isNaN(date.getTime())) return { date, year: parseInt(y, 10), month: parseInt(m, 10) - 1, day: parseInt(d, 10) };
    }

    // 4. Fallback to native Date parser (e.g. "Sep 14, 2026")
    const fallback = new Date(trimmed);
    if (!isNaN(fallback.getTime())) {
        return { date: fallback, year: fallback.getFullYear(), month: fallback.getMonth(), day: fallback.getDate() };
    }

    return null;
};

const getEventTimingInfo = (artifact, lnT) => {
    if (getType(artifact) !== 'event') return null;
    const metadataDate = Array.isArray(artifact.metadata)
        ? artifact.metadata.find(m => m?.key === 'date' || m?.key === 'startDate')?.value
        : null;
    const rawValue = artifact.event?.startDate || artifact.event?.rawDate || metadataDate;
    const dateParts = parseEventDate(rawValue);
    if (!dateParts) return null;

    const month = format(dateParts.date, 'MMM').toUpperCase();
    const day = format(dateParts.date, 'd');
    const formattedDate = format(dateParts.date, 'EEE, MMM d, yyyy');

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateParts.date);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let timing = '';
    let isPast = false;
    if (diffDays < 0) {
        timing = lnT('messages:passed', 'passed');
        isPast = true;
    } else if (diffDays === 0) {
        timing = lnT('messages:today', 'today');
    } else if (diffDays === 1) {
        timing = lnT('messages:tomorrow', 'tomorrow');
    } else {
        timing = lnT('messages:inDays', { count: diffDays, defaultValue: `in ${diffDays} days` });
    }

    return {
        month,
        day,
        formattedDate,
        startTime: artifact.event?.startTime || null,
        timing,
        isPast
    };
};

const placeDetails = artifact => {
    if (getType(artifact) === 'event') return [artifact.event?.startDate || artifact.event?.rawDate, artifact.event?.startTime].filter(Boolean);
    if (getType(artifact) !== 'place') return [];
    const data = Array.isArray(artifact.metadata) ? artifact.metadata : [];
    const value = key => data.find(item => item?.key === key)?.value;
    return [value('category'), value('rating') ? `★ ${value('rating')}` : null].filter(Boolean);
};

const ExtractedArtifactsSection = ({ artifacts }) => {
    const { t: lnT } = useTranslation();

    const [selectedArtifact, setSelectedArtifact] = React.useState(null);
    const [unavailableImages, setUnavailableImages] = React.useState({});
    const visibleArtifacts = React.useMemo(() => {
        const seen = new Set();
        return (Array.isArray(artifacts) ? artifacts : []).filter(artifact => {
            const id = String(artifact?.id || '');
            if (!id || !String(artifact?.title || '').trim() || seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [lnT, artifacts]);

    React.useEffect(() => {
        if (selectedArtifact) {
            const current = visibleArtifacts.find(artifact => artifact.id === selectedArtifact.id);
            if (current !== selectedArtifact) setSelectedArtifact(current || null);
        }
    }, [selectedArtifact, visibleArtifacts]);

    if (!visibleArtifacts.length) return null;
    const type = visibleArtifacts.every(artifact => getType(artifact) === getType(visibleArtifacts[0])) ? getType(visibleArtifacts[0]) : null;
    const label = type && ENTITY_LABELS[type] ? (visibleArtifacts.length === 1 ? ENTITY_LABELS[type] : ENTITY_PLURAL_LABELS[type]) : (visibleArtifacts.length === 1 ? lnT('common:extraction') : lnT('messages:extractions'));
    const theme = themeFor(type ? visibleArtifacts[0] : null);
    const showHeader = visibleArtifacts.length > 1;

    return (
        <Section data-message-interactive="true">
            {showHeader ? (
                <Header>
                    <Label>{label}</Label>
                    <Count $theme={theme}>{visibleArtifacts.length}</Count>
                </Header>
            ) : null}
            <List>
                {visibleArtifacts.map(artifact => {
                    const artifactTheme = themeFor(artifact);
                    const eventInfo = getEventTimingInfo(artifact, lnT);
                    const hasImage = artifact.image && !unavailableImages[artifact.image];

                    return (
                        <Row
                            key={artifact.id}
                            type="button"
                            $theme={artifactTheme}
                            onClick={event => {
                                event.stopPropagation();
                                setSelectedArtifact(artifact);
                            }}
                            title={lnT('auth:open', { value1: String(artifact.title) })}
                        >
                            {hasImage ? (
                                <Image
                                    src={artifact.image}
                                    alt=""
                                    onError={() => setUnavailableImages(previous => ({ ...previous, [artifact.image]: true }))}
                                />
                            ) : eventInfo ? (
                                <DateTile $theme={artifactTheme}>
                                    <DateMonth $theme={artifactTheme}>{eventInfo.month}</DateMonth>
                                    <DateDay>{eventInfo.day}</DateDay>
                                </DateTile>
                            ) : (
                                <Placeholder $theme={artifactTheme}>{iconFor(artifact, 18)}</Placeholder>
                            )}

                            <Copy>
                                <Name>{artifact.title}</Name>
                                <Meta>
                                    {eventInfo ? (
                                        <>
                                            <span>
                                                {eventInfo.formattedDate}
                                                {eventInfo.startTime ? ` · ${eventInfo.startTime}` : ''}
                                            </span>
                                            {eventInfo.timing ? (
                                                <TimingText $theme={artifactTheme} $isUpcoming={!eventInfo.isPast}>
                                                    ({eventInfo.timing})
                                                </TimingText>
                                            ) : null}
                                        </>
                                    ) : (
                                        <>
                                            {iconFor(artifact, 12)}
                                            {placeDetails(artifact).join(' · ') || (ENTITY_LABELS[getType(artifact)] || lnT('common:extraction'))}
                                        </>
                                    )}
                                </Meta>
                                {artifact.summary ? <Summary>{artifact.summary}</Summary> : null}
                            </Copy>
                            <FiChevronRight size={15} aria-hidden="true" style={{ opacity: 0.65, flexShrink: 0 }} />
                        </Row>
                    );
                })}
            </List>
            <ArtifactDetailModal artifact={selectedArtifact} onRequestClose={() => setSelectedArtifact(null)} />
        </Section>
    );
};

const Section = styled.section`
    margin-top: 10px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 8px;
`;

const Label = styled.h3`
    margin: 0;
    color: rgba(255, 255, 255, 0.62);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const Count = styled.span`
    display: inline-flex;
    min-width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 999px;
    background: ${p => p.$theme.background};
    color: ${p => p.$theme.color};
    font-size: 10px;
    font-weight: 700;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Row = styled.button`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border: 1px solid transparent;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.88);
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.11);
    }
`;

const DateTile = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: ${p => p.$theme.background};
    border: 1px solid ${p => p.$theme.border};
`;

const DateMonth = styled.span`
    color: ${p => p.$theme.color};
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
`;

const DateDay = styled.span`
    color: #ffffff;
    font-size: 17px;
    font-weight: 700;
    line-height: 1;
`;

const Placeholder = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: ${p => p.$theme.background};
    border: 1px solid ${p => p.$theme.border};
    color: ${p => p.$theme.color};
`;

const Image = styled.img`
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.06);
`;

const Copy = styled.span`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
`;

const Name = styled.span`
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.95);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
`;

const Meta = styled.span`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    color: rgba(255, 255, 255, 0.65);
    font-size: 12px;
    line-height: 1.25;
`;

const TimingText = styled.span`
    color: ${p => (p.$isUpcoming ? (p.$theme?.color || '#e8b96b') : 'rgba(255, 255, 255, 0.65)')};
    font-weight: ${p => (p.$isUpcoming ? '600' : 'normal')};
    margin-left: 2px;
`;

const Summary = styled.span`
    max-width: 100%;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.46);
    font-size: 11px;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export default ExtractedArtifactsSection;
